/**
 * Local parsing of everything the operator scans or types (Req 11.3, 14.7, 28.3). Mirrors
 * the contract's input patterns (openapi.yaml) without pulling zod into the bundle; the
 * API validates again.
 *
 * A camera or keyboard-wedge scan is classified by its text:
 *  - `MQR1.<jwt>.<totp>`                  → Member QR (identifies or confirms a member);
 *  - `https://{qrHost}/c/{code}`          → product code QR (`toQrUrl` format, Req 11.7);
 *  - a UUID                               → order id (handover);
 *  - anything else that parses            → typed or printed Short_Code.
 */
import { format, normalise, parse, parseQr, type ShortCode } from '@macksons/shortcode';

/** `MemberQrPayload` pattern. */
export const MEMBER_QR_RE = /^MQR1\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[0-9]{6}$/;
const MEMBER_QR_MAX = 2048;
/** `PhoneInput`: as typed; normalised server-side. */
const PHONE_RE = /^\+?[0-9 ()-]{7,20}$/;
/** `InvoiceNo`. */
const INVOICE_NO_RE = /^[A-Za-z0-9/_.-]{1,64}$/;
/** `OtpCode`. */
const OTP_RE = /^[0-9]{6}$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Characters a Short_Code can contain after `normalise` (payload alphabet + check symbols). */
const CODE_CHARS_RE = /^[0-9A-Z*~$=]*$/;
const CODE_LENGTH = 12;

export type ScanInput =
  | { kind: 'member_qr'; token: string }
  | { kind: 'product'; code: ShortCode; display: string }
  | { kind: 'order'; orderId: string }
  | { kind: 'invalid'; reason: InvalidScanReason };

/**
 * - `member_qr`: starts like a Member QR but is malformed;
 * - `qr_host`: a product QR URL for another host;
 * - `typo`: looks like a Short_Code but fails the check (Req 11.3);
 * - `unknown`: none of the accepted forms.
 */
export type InvalidScanReason = 'member_qr' | 'qr_host' | 'typo' | 'unknown';

export function isMemberQr(text: string): boolean {
  return text.length <= MEMBER_QR_MAX && MEMBER_QR_RE.test(text);
}

const product = (code: ShortCode): ScanInput => ({ kind: 'product', code, display: format(code) });

export function classifyScan(raw: string, qrHost: string): ScanInput {
  const text = raw.trim();
  if (text === '') return { kind: 'invalid', reason: 'unknown' };
  if (/^MQR1\./i.test(text)) return isMemberQr(text) ? { kind: 'member_qr', token: text } : { kind: 'invalid', reason: 'member_qr' };
  if (/^[a-z][a-z0-9+.-]*:/i.test(text)) {
    const qr = parseQr(text, qrHost);
    if (qr.ok) return product(qr.code);
    if (qr.error !== 'url') return { kind: 'invalid', reason: 'typo' };
    // Well-formed product QR path on a different host (another environment's labels).
    try {
      const u = new URL(text);
      if (u.protocol === 'https:' && u.pathname.startsWith('/c/')) return { kind: 'invalid', reason: 'qr_host' };
    } catch {
      // fall through
    }
    return { kind: 'invalid', reason: 'unknown' };
  }
  if (UUID_RE.test(text)) return { kind: 'order', orderId: text.toLowerCase() };
  const parsed = parse(text);
  if (parsed.ok) return product(parsed.code);
  const n = normalise(text);
  return { kind: 'invalid', reason: n.length === CODE_LENGTH && CODE_CHARS_RE.test(n) ? 'typo' : 'unknown' };
}

export type TypedCodeState =
  | { state: 'empty' }
  | { state: 'incomplete'; remaining: number }
  | { state: 'valid'; code: ShortCode; display: string }
  | { state: 'invalid' };

/**
 * Live check of a typed Short_Code: incomplete while fewer than 12 valid symbols are
 * typed, then valid or invalid (format or check failure, i.e. a typo).
 */
export function checkTypedCode(input: string): TypedCodeState {
  const n = normalise(input);
  if (n === '') return { state: 'empty' };
  if (!CODE_CHARS_RE.test(n) || n.length > CODE_LENGTH) return { state: 'invalid' };
  if (n.length < CODE_LENGTH) return { state: 'incomplete', remaining: CODE_LENGTH - n.length };
  const parsed = parse(n);
  return parsed.ok ? { state: 'valid', code: parsed.code, display: format(parsed.code) } : { state: 'invalid' };
}

export const isPhone = (s: string): boolean => PHONE_RE.test(s.trim());
export const isInvoiceNo = (s: string): boolean => INVOICE_NO_RE.test(s.trim());
export const isOtp = (s: string): boolean => OTP_RE.test(s.trim());
export const isUuid = (s: string): boolean => UUID_RE.test(s.trim());

const MAX_DIGITS = 19;

/**
 * LKR amount as typed (`2500`, `2,500.50`, `2500.5`) → positive minor units (cents) as a
 * decimal string (`PositiveMinorAmount`), or `undefined`. Exact: no floating point.
 */
export function lkrToMinor(input: string): string | undefined {
  const s = input.trim().replace(/[,\s]/g, '');
  const m = /^(\d+)(?:\.(\d{1,2}))?$/.exec(s);
  if (!m) return undefined;
  const minor = BigInt(m[1]!) * 100n + BigInt((m[2] ?? '').padEnd(2, '0'));
  if (minor <= 0n) return undefined;
  const out = minor.toString();
  return out.length > MAX_DIGITS ? undefined : out;
}

/** Positive integer points as typed (`1500`, `1,500`) → `PositivePoints` string, or `undefined`. */
export function toPositivePoints(input: string): string | undefined {
  const s = input.trim().replace(/[,\s]/g, '');
  if (!/^\d+$/.test(s)) return undefined;
  const out = BigInt(s).toString();
  return out === '0' || out.length > MAX_DIGITS ? undefined : out;
}
